import type { PlayerId, RoomPlayer } from '@hanamikoji/shared';
import type {
  AuthoritativeEventLogEntry,
  HostRuntimePlayerSnapshot,
  HostRuntimeSnapshot,
} from './HostRuntime';
import {
  P2P_PROTOCOL_VERSION,
  type P2PActionIntentPayload,
  type P2PEnvelope,
  type P2PMessageType,
} from './protocol';
import { isHighEntropyToken } from './crypto';

export const HOST_RUNTIME_SNAPSHOT_VERSION = 1;
export const CLIENT_SESSION_VERSION = 1;

const SUPPORTED_MESSAGE_TYPES = new Set<string>([
  'HELLO',
  'JOIN_REQUEST',
  'JOIN_ACCEPT',
  'JOIN_REJECT',
  'LOBBY_STATE',
  'LOBBY_READY',
  'START_GAME_REQUEST',
  'START_GAME_REJECTED',
  'GAME_START',
  'ACTION_INTENT',
  'ACTION_ACCEPTED',
  'ACTION_REJECTED',
  'STATE_VIEW',
  'SYNC_REQUEST',
  'SYNC_RESPONSE',
  'PLAYER_DISCONNECTED',
  'PLAYER_RECONNECTED',
  'ERROR',
] satisfies P2PMessageType[]);

const ACTION_REJECT_CODES = new Set(['NOT_JOINED', 'ACTOR_MISMATCH', 'STALE_STATE', 'INVALID_ACTION', 'GAME_NOT_STARTED', 'UNKNOWN']);
const MAX_STRING_FIELD_LENGTH = 16_384;

export interface P2PEnvelopeValidationOptions {
  expectedFromPeerId?: string;
  expectedToPeerId?: string;
}

export function parseP2PEnvelopeFrame(frame: unknown, options: P2PEnvelopeValidationOptions = {}): P2PEnvelope | null {
  if (typeof frame !== 'string') return null;
  try {
    return validateP2PEnvelope(JSON.parse(frame) as unknown, options);
  } catch {
    return null;
  }
}

export function validateP2PEnvelope(value: unknown, options: P2PEnvelopeValidationOptions = {}): P2PEnvelope | null {
  if (!isRecord(value)) return null;
  if (!isNonEmptyString(value.fromPeerId) || !isNonEmptyString(value.toPeerId)) return null;
  if (options.expectedFromPeerId && value.fromPeerId !== options.expectedFromPeerId) return null;
  if (options.expectedToPeerId && value.toPeerId !== options.expectedToPeerId) return null;
  if (!isValidP2PMessage(value.message)) return null;
  return value as unknown as P2PEnvelope;
}

export function isValidP2PMessage(value: unknown): boolean {
  if (!isRecord(value)) return false;
  if (!isNonEmptyString(value.type)) return false;
  if (!SUPPORTED_MESSAGE_TYPES.has(value.type)) return false;
  if (value.protocolVersion !== P2P_PROTOCOL_VERSION) return false;

  switch (value.type) {
    case 'HELLO':
      return isOptionalString(value.clientName);
    case 'JOIN_REQUEST':
      return typeof value.clientName === 'string'
        && isOptionalString(value.requestedRoomId)
        && isOptionalPlayerId(value.requestedPlayerId)
        && isOptionalReconnectToken(value.reconnectToken)
        && isOptionalNonNegativeInteger(value.lastStateVersion)
        && isOptionalString(value.lastViewHash);
    case 'JOIN_ACCEPT':
      return isNonEmptyString(value.roomId)
        && isPlayerId(value.playerId)
        && isHighEntropyToken(value.reconnectToken)
        && isRoomPlayers(value.players)
        && isOptionalBoolean(value.resumed);
    case 'JOIN_REJECT':
      return isNonEmptyString(value.reason) && isOptionalBoolean(value.canRetry);
    case 'LOBBY_STATE':
      return isNonEmptyString(value.roomId)
        && isRoomPlayers(value.players)
        && isReadyRecord(value.ready)
        && typeof value.canStart === 'boolean'
        && isPlayerId(value.hostPlayerId);
    case 'LOBBY_READY':
      return isPlayerId(value.playerId) && typeof value.ready === 'boolean';
    case 'START_GAME_REQUEST':
      return isPlayerId(value.actorId);
    case 'START_GAME_REJECTED':
      return isNonEmptyString(value.reason);
    case 'GAME_START':
      return isStateEnvelopeMessage(value)
        && isHighEntropyToken(value.reconnectToken)
        && isRoomPlayers(value.players);
    case 'ACTION_INTENT':
      return isNonEmptyString(value.requestId)
        && isPlayerId(value.actorId)
        && isNonNegativeInteger(value.stateVersion)
        && isNonEmptyString(value.previousViewHash)
        && isValidActionIntent(value.intent);
    case 'ACTION_ACCEPTED':
      return isNonEmptyString(value.requestId)
        && isPlayerId(value.actorId)
        && isNonNegativeInteger(value.previousStateVersion)
        && isNonNegativeInteger(value.stateVersion);
    case 'ACTION_REJECTED':
      return isNonEmptyString(value.requestId)
        && isOptionalPlayerId(value.actorId)
        && isNonEmptyString(value.reason)
        && typeof value.code === 'string'
        && ACTION_REJECT_CODES.has(value.code)
        && typeof value.canSync === 'boolean'
        && isOptionalNonNegativeInteger(value.expectedStateVersion)
        && isOptionalString(value.expectedPreviousViewHash);
    case 'STATE_VIEW':
    case 'SYNC_RESPONSE':
      return isStateEnvelopeMessage(value);
    case 'SYNC_REQUEST':
      return isPlayerId(value.playerId)
        && isOptionalNonNegativeInteger(value.stateVersion)
        && isOptionalString(value.previousViewHash);
    case 'PLAYER_DISCONNECTED':
      return isPlayerId(value.playerId) && isOptionalString(value.reason);
    case 'PLAYER_RECONNECTED':
      return isPlayerId(value.playerId);
    case 'ERROR':
      return isNonEmptyString(value.code) && isNonEmptyString(value.message) && isOptionalBoolean(value.canSync);
    default:
      return false;
  }
}

function isStateEnvelopeMessage(value: Record<string, unknown>): boolean {
  return isNonEmptyString(value.roomId)
    && isPlayerId(value.playerId)
    && isNonNegativeInteger(value.stateVersion)
    && isNonEmptyString(value.viewHash)
    && isOptionalNonNegativeInteger(value.previousStateVersion)
    && isOptionalString(value.previousViewHash)
    && isRecord(value.state);
}

export function normalizeHostRuntimeSnapshot(value: unknown): HostRuntimeSnapshot | null {
  if (!isRecord(value)) return null;
  if (value.version !== HOST_RUNTIME_SNAPSHOT_VERSION) return null;
  if (!isNonEmptyString(value.roomId)) return null;
  if (!isNonNegativeInteger(value.seed)) return null;
  if (!isPlayerId(value.firstPlayer)) return null;
  if (value.hostPlayerId !== undefined && !isPlayerId(value.hostPlayerId)) return null;
  if (!isNonNegativeInteger(value.stateVersion)) return null;
  if (value.engineState !== null && !isRecord(value.engineState)) return null;
  if (!Array.isArray(value.eventLog)) return null;
  if (!Array.isArray(value.players)) return null;
  if (value.players.length > 2) return null;

  const players: HostRuntimePlayerSnapshot[] = [];
  const seenPlayers = new Set<PlayerId>();
  for (const player of value.players) {
    const normalized = normalizeHostRuntimePlayerSnapshot(player);
    if (!normalized || seenPlayers.has(normalized.playerId)) return null;
    seenPlayers.add(normalized.playerId);
    players.push(normalized);
  }

  const eventLog: AuthoritativeEventLogEntry[] = [];
  for (const entry of value.eventLog) {
    const normalized = normalizeEventLogEntry(entry);
    if (!normalized) return null;
    eventLog.push(normalized);
  }

  return {
    version: HOST_RUNTIME_SNAPSHOT_VERSION,
    roomId: value.roomId,
    seed: value.seed,
    firstPlayer: value.firstPlayer,
    hostPlayerId: (value.hostPlayerId ?? 'p1') as PlayerId,
    lobbyReady: normalizeReadyRecord(value.lobbyReady),
    stateVersion: value.stateVersion,
    engineState: value.engineState as HostRuntimeSnapshot['engineState'],
    eventLog,
    players,
  };
}

function normalizeHostRuntimePlayerSnapshot(value: unknown): HostRuntimePlayerSnapshot | null {
  if (!isRecord(value)) return null;
  if (!isPlayerId(value.playerId)) return null;
  if (!isNonEmptyString(value.name)) return null;
  if (!isHighEntropyToken(value.reconnectToken)) return null;
  return {
    playerId: value.playerId,
    name: value.name.slice(0, 24),
    reconnectToken: value.reconnectToken,
  };
}

function normalizeEventLogEntry(value: unknown): AuthoritativeEventLogEntry | null {
  if (!isRecord(value)) return null;
  if (!isNonNegativeInteger(value.sequence)) return null;
  if (!isPlayerId(value.actorId)) return null;
  if (!isNonEmptyString(value.requestId)) return null;
  if (!isValidActionIntent(value.intent)) return null;
  if (!isNonNegativeInteger(value.previousVersion) || !isNonNegativeInteger(value.nextVersion)) return null;
  if (!isNonEmptyString(value.previousAuthoritativeStateHash) || !isNonEmptyString(value.nextAuthoritativeStateHash)) return null;
  if (!isNonEmptyString(value.previousViewHash)) return null;
  if (!isRecord(value.nextViewHashes) || !isNonEmptyString(value.nextViewHashes.p1) || !isNonEmptyString(value.nextViewHashes.p2)) return null;

  return {
    sequence: value.sequence,
    actorId: value.actorId,
    requestId: value.requestId,
    intent: cloneActionIntent(value.intent),
    previousVersion: value.previousVersion,
    nextVersion: value.nextVersion,
    previousAuthoritativeStateHash: value.previousAuthoritativeStateHash,
    nextAuthoritativeStateHash: value.nextAuthoritativeStateHash,
    previousViewHash: value.previousViewHash,
    nextViewHashes: {
      p1: value.nextViewHashes.p1,
      p2: value.nextViewHashes.p2,
    },
  };
}

function cloneActionIntent(intent: P2PActionIntentPayload): P2PActionIntentPayload {
  switch (intent.type) {
    case 'DRAW_CARD':
      return { type: 'DRAW_CARD' };
    case 'RESOLVE_ACTION':
      return { type: 'RESOLVE_ACTION', selection: intent.selection };
    case 'PLAY_ACTION':
      return {
        type: 'PLAY_ACTION',
        actionType: intent.actionType,
        cardIds: [...intent.cardIds],
        grouping: intent.grouping?.map(group => [...group]),
      };
  }
}

function isValidActionIntent(value: unknown): value is P2PActionIntentPayload {
  if (!isRecord(value) || !isNonEmptyString(value.type)) return false;
  switch (value.type) {
    case 'DRAW_CARD':
      return true;
    case 'PLAY_ACTION':
      return isNonEmptyString(value.actionType)
        && isStringArray(value.cardIds)
        && (value.grouping === undefined || isStringMatrix(value.grouping));
    case 'RESOLVE_ACTION':
      return Number.isInteger(value.selection);
    default:
      return false;
  }
}

function isRoomPlayers(value: unknown): value is RoomPlayer[] {
  return Array.isArray(value)
    && value.length <= 2
    && value.every(player => isRecord(player)
      && isPlayerId(player.playerId)
      && isNonEmptyString(player.name)
      && isNonEmptyString(player.socketId));
}

function normalizeReadyRecord(value: unknown): Record<PlayerId, boolean> {
  if (!isRecord(value)) return { p1: false, p2: false };
  return { p1: Boolean(value.p1), p2: Boolean(value.p2) };
}

function isReadyRecord(value: unknown): value is Record<PlayerId, boolean> {
  return isRecord(value) && typeof value.p1 === 'boolean' && typeof value.p2 === 'boolean';
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.length <= 16 && value.every(isNonEmptyString);
}

function isStringMatrix(value: unknown): value is string[][] {
  return Array.isArray(value) && value.length <= 16 && value.every(isStringArray);
}

function isOptionalReconnectToken(value: unknown): boolean {
  return value === undefined || isHighEntropyToken(value);
}

function isOptionalPlayerId(value: unknown): value is PlayerId | undefined {
  return value === undefined || isPlayerId(value);
}

function isPlayerId(value: unknown): value is PlayerId {
  return value === 'p1' || value === 'p2';
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

function isOptionalBoolean(value: unknown): value is boolean | undefined {
  return value === undefined || typeof value === 'boolean';
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= MAX_STRING_FIELD_LENGTH;
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

function isOptionalNonNegativeInteger(value: unknown): value is number | undefined {
  return value === undefined || isNonNegativeInteger(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
